import { cn } from '@lib/utils';

export type SimpleTab = {
  badge?: number;
  icon?: React.ReactNode;
  label: React.ReactNode;
  value: string;
};

type SimpleTabsProps = {
  activeTab: string;
  className?: string;
  fullWidth?: boolean;
  onTabChange: (value: string) => void;
  resetScrollOnNavigation?: boolean;
  tabs: SimpleTab[];
  variant?: 'attached' | 'default' | 'underline';
};

const scrollToTop = (): void => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export function SimpleTabs(props: SimpleTabsProps) {
  const {
    activeTab,
    className,
    fullWidth = true,
    onTabChange,
    resetScrollOnNavigation = false,
    tabs,
    variant = 'default',
  } = props;

  const isAttached = variant === 'attached';

  const handleTabClick = (value: string) => {
    if (value === activeTab) {
      return;
    }

    if (resetScrollOnNavigation) {
      scrollToTop();
    }
    onTabChange(value);
  };

  if (isAttached) {
    return (
      <div
        role="tablist"
        className={cn('relative flex items-end gap-1', fullWidth ? 'w-full' : 'w-fit', className)}
      >
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.value;
          const isFirst = index === 0;
          const isLast = index === tabs.length - 1;

          return (
            <button
              key={tab.value}
              type="button"
              aria-selected={isActive}
              role="tab"
              onClick={() => handleTabClick(tab.value)}
              className={cn(
                'relative flex cursor-pointer items-center justify-center text-sm font-medium text-center transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'rounded-t-lg',
                'px-8 py-2.5 min-w-40',
                isFirst && 'rounded-tl-xl',
                isLast && 'rounded-tr-xl',
                fullWidth && 'flex-1',
                isActive
                  ? 'bg-[#F0F0F0] text-[#3D2EFF]'
                  : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700',
              )}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {tab.icon && <span className="mr-2">{tab.icon}</span>}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (variant === 'underline') {
    return (
      <div
        role="tablist"
        className={cn('flex flex-wrap items-end gap-0 border-b border-gray-200', className)}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.value;

          return (
            <button
              key={tab.value}
              type="button"
              aria-selected={isActive}
              role="tab"
              onClick={() => handleTabClick(tab.value)}
              className={cn(
                '-mb-px inline-flex cursor-pointer items-center gap-2 border-b-3 px-3 py-2 text-sm transition-colors focus-visible:outline-none',
                isActive
                  ? 'border-primary font-medium text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-900',
              )}
            >
              {tab.icon && <span className="flex items-center">{tab.icon}</span>}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn('relative flex bg-white p-1.5 rounded-xl gap-1', fullWidth ? 'w-full' : 'w-fit', className)}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.value;

        return (
          <button
            key={tab.value}
            type="button"
            aria-selected={isActive}
            role="tab"
            onClick={() => handleTabClick(tab.value)}
            className={cn(
              'relative flex cursor-pointer items-center justify-center px-6 py-2 text-sm font-medium text-center transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'rounded-md min-w-52',
              fullWidth && 'flex-1',
              isActive ? 'text-[#3D2EFF] bg-[#3D2EFF1A]' : 'text-gray-600 hover:text-gray-900 bg-gray-100',
            )}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {tab.icon && <span className="mr-2">{tab.icon}</span>}
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
